import { Action } from 'redux';
import { pages } from './pages';
import { preference } from './preference';
import { integratedDrawing, drawingHistory } from './integratedDrawing';
import {
  appReducer,
  AppStates,
  initAppStates,
  prepareWorkspaceSaving,
  startWorkspaceSaving,
  AppPictureStorage,
} from './app';
import { view } from './view';
import { speech } from '../tts/reducers/speech';
import { uimode } from './uimode';
import { isPreferenceAction } from '../actions/PreferenceAction';
import { isDrawingAction } from '../drawing/actions/DrawingAction';
import { isIntegratedDrawingAction } from '../actions/IntegratedDrawingAction';
import { isUIModeAction } from '../actions/UIModeAction';
import { isPageAction } from '../actions/PageAction';
import { isTTSAction } from '../tts/actions/TTSAction';
import { isViewAction } from '../actions/ViewAction';
import { isAppAction, APP_REPLACE_STATES, APP_SET_COMPOSER_MODE } from '../actions/AppAction';
import { verifyAppStates } from './verify';
import { reduceViewByPageAction } from './viewPage';
import DrawingToolAction from '../drawing/actions/DrawingToolAction';
import drawingTool from '../drawing/reducers/drawingTool';
import HistoryAction from '../actions/HistoryAction';
import AutoSavingAction from '../actions/AutoSavingAction';
import autoSaving, {
  applyModification,
  didAutoSavingStart,
  didAutoSavingRestart,
} from './autoSaving';
import {
  getEditorKeyAndPage,
  createIsPictureUsed,
  createIsImageDemanded,
} from '../helpers/integratedDrawings';
import ContentStorageAction from '../actions/ContentStorageAction';
import contentStorage from './contentStorage';
import { isMarkerAction } from '../marker/actions/MarkerAction';
import { markerReducer } from '../marker/reducers/marker';
import pictureStorage, {
  prunePictureItems,
  preparePictureItemsForDrawing,
} from '../drawing/reducers/pictureStorage';
import PictureStorageAction from '../drawing/actions/PictureStorageAction';
import { isReadyToSaveWorkspace } from '../userdata/save';
import { reducePictureStorageByDrawingAction } from '../drawing/reducers/drawingPicture';
import { isSelectionAction } from '../actions/SelectionAction';
import { selection } from './selection';
import { reducePagesByViewAction } from './pageView';
import { isBookmarkAction } from '../bookmark/actions/BookmarkAction';
import { bookmarkReducer } from '../bookmark/reducers/bookmark';
import UIAction from '../actions/ui/UIAction';
import { ui } from './ui/ui';
import { composer } from '../clipbook/reducers/composer';
import { isComposerAction } from '../clipbook/actions/ComposerAction';

function doReduceAppStates(state: Readonly<AppStates>, action: Readonly<Action>): AppStates {
  if (
    isComposerAction(action)
    || state.composerMode && action.type !== APP_SET_COMPOSER_MODE
  ) {
    return { ...state, composer: composer(state.composer, action) };
  }

  if (isPageAction(action)) {
    const prevSubState = state.pages;
    const prevViewState = state.view;
    const publication = state.publication!;
    const nextSubState = pages(prevSubState, publication, action);
    const nextViewState = reduceViewByPageAction(nextSubState, prevViewState, publication, action);
    if (nextSubState === prevSubState && nextViewState === prevViewState) {
      return state;
    }
    return { ...state, pages: nextSubState, view: nextViewState };
  }

  if (isViewAction(action)) {
    const {
      publication, view: prevSubState,
      pages: pageStates, // Workaround for Webpack4.1.x 'es2015' module code generation
      uimode: uiState,
    } = state;
    const nextSubState = view(prevSubState, action, pageStates, publication!, uiState.mode);
    const nextPageState = reducePagesByViewAction(pageStates, action);
    if (nextSubState === prevSubState && nextPageState === pageStates) {
      return state;
    }
    return { ...state, view: nextSubState, pages: nextPageState };
  }

  if (isPreferenceAction(action)) {
    const prevSubState = state.preference;
    const nextSubState = preference(prevSubState, action);
    return nextSubState === prevSubState ? state : { ...state, preference: nextSubState };
  }

  if (isDrawingAction(action) || isIntegratedDrawingAction(action)) {
    if (!state.publication) {
      throw new Error('Illegal state.');
    }

    const index = getEditorKeyAndPage(state.pages, state.publication);
    const prevSubState = state.integratedDrawing;
    const nextSubState = integratedDrawing(prevSubState, index, action);
    if (nextSubState === prevSubState) {
      return state;
    }

    // TODO : To take robustness, this process may should be done by the root reducer's phase.
    let nextPictureStorage = state.pictureStorage;
    if (isDrawingAction(action)) {
      nextPictureStorage = reducePictureStorageByDrawingAction(nextPictureStorage, action);
    }

    return {
      ...state,
      pictureStorage: nextPictureStorage,
      integratedDrawing: nextSubState,
    };
  }

  if (HistoryAction.isDomesticAction(action)) {
    if (!state.publication) {
      throw new Error('Illegal state.');
    }

    const index = getEditorKeyAndPage(state.pages, state.publication);
    const prevSubState = state.integratedDrawing;
    const nextSubState = drawingHistory(prevSubState, index, action);
    return nextSubState === prevSubState ? state : { ...state, integratedDrawing: nextSubState };
  }

  if (DrawingToolAction.isDomesticAction(action)) {
    const prevSubState = state.drawingTool;
    const nextSubState = drawingTool(prevSubState, action);
    return nextSubState === prevSubState ? state : { ...state, drawingTool: nextSubState };
  }

  if (isUIModeAction(action)) {
    const prevSubState = state.uimode;
    const nextSubState = uimode(prevSubState, action);
    return nextSubState === prevSubState ? state : { ...state, uimode: nextSubState };
  }

  if (UIAction.isDomesticAction(action)) {
    const prevSubState = state.ui;
    const nextSubState = ui(prevSubState, action);
    return nextSubState === prevSubState ? state : { ...state, ui: nextSubState };
  }

  if (isTTSAction(action)) {
    const prevSubState = state.speech;
    const nextSubState = speech(prevSubState, action);
    return nextSubState === prevSubState ? state : { ...state, speech: nextSubState };
  }

  if (PictureStorageAction.isDomesticAction<HTMLCanvasElement>(action, AppPictureStorage.Drawing)) {
    const prevSubState = state.pictureStorage;
    const nextSubState = pictureStorage(prevSubState, action);
    return nextSubState === prevSubState ? state : { ...state, pictureStorage: nextSubState };
  }

  if (AutoSavingAction.isDomesticAction(action)) {
    const prevSubState = state.autoSaving;
    const nextSubState = autoSaving(prevSubState, action);
    return nextSubState === prevSubState ? state : { ...state, autoSaving: nextSubState };
  }

  if (ContentStorageAction.isDomesticAction(action)) {
    const prevSubState = state.contentStorage;
    const nextSubState = contentStorage(prevSubState, action);
    return nextSubState === prevSubState ? state : { ...state, contentStorage: nextSubState };
  }

  if (isAppAction(action)) {
    return appReducer(state, action);
  }

  if (isMarkerAction(action)) {
    return markerReducer(state, action);
  }

  if (isBookmarkAction(action)) {
    const prevSubState = state.bookmarks;
    const nextSubState = bookmarkReducer(prevSubState, action);
    return nextSubState === prevSubState ? state : { ...state, bookmarks: nextSubState };
  }

  if (isSelectionAction(action)) {
    return {
      ...state,
      selectionState: selection(state.selectionState, action),
    };
  }

  // Normally not reached, but some Redux built-in actions (such as "@@redux/INIT") can be given.
  return state;
}

const rootReducer = (
  state: Readonly<AppStates> = initAppStates(),
  action: Readonly<Action>,
): AppStates => {
  let intermediateState = doReduceAppStates(state, action);

  // TODO : Remove on release build.
  verifyAppStates(intermediateState);

  if (action.type === APP_REPLACE_STATES) {
    return intermediateState;
  }

  if (state === intermediateState) {
    return intermediateState;
  }

  let nextPictureStorage = intermediateState.pictureStorage;

  if (
    intermediateState.publication !== undefined
    && (
      state.pages.tabs !== intermediateState.pages.tabs
      || state.integratedDrawing !== intermediateState.integratedDrawing
    )
  ) {
    nextPictureStorage = preparePictureItemsForDrawing(
      nextPictureStorage,
      createIsImageDemanded(
        intermediateState.integratedDrawing,
        intermediateState.pages,
        intermediateState.publication,
      ),
    );
  }

  if (state.integratedDrawing !== intermediateState.integratedDrawing) {
    nextPictureStorage = prunePictureItems(
      nextPictureStorage,
      createIsPictureUsed(intermediateState.integratedDrawing),
    );
  }

  if (intermediateState.pictureStorage !== nextPictureStorage) {
    intermediateState = {
      ...intermediateState,
      pictureStorage: nextPictureStorage,
    };
  }

  if (
    state.pages !== intermediateState.pages
    || state.preference !== intermediateState.preference
    || state.speech.rate !== intermediateState.speech.rate
    || state.integratedDrawing.currentData !== intermediateState.integratedDrawing.currentData
  ) {
    const nextAutoSaving = applyModification(intermediateState.autoSaving);
    if (intermediateState.autoSaving !== nextAutoSaving) {
      intermediateState = { ...intermediateState, autoSaving: nextAutoSaving };
    }
  }

  if (
    didAutoSavingStart(state.autoSaving, intermediateState.autoSaving)
    || didAutoSavingRestart(state.autoSaving, intermediateState.autoSaving)
  ) {
    intermediateState = prepareWorkspaceSaving(intermediateState);
  }

  if (
    isReadyToSaveWorkspace(intermediateState.savingWorkspace, intermediateState.pictureStorage)
    && (
      state.savingWorkspace !== intermediateState.savingWorkspace
      || !isReadyToSaveWorkspace(state.savingWorkspace, state.pictureStorage)
    )
  ) {
    intermediateState = startWorkspaceSaving(intermediateState);
  }

  return intermediateState;
};

export const createRootReducer = () => rootReducer;
